/**
 * Single source of truth for the exception-lifecycle URL ?status= value.
 *
 * Two levels of resolution:
 *
 * - `ExceptionStatus` (parent group) - what the binary Open/Closed pill
 *   reads. Always 'open' or 'closed'.
 * - `ExceptionStatusValue` (sub-status) - what the summary chips
 *   underneath the pill read/write. One of 'open' | 'needs_review' |
 *   'blocked' | 'closed' | 'resolved' | 'ignored'.
 *
 * The URL carries the sub-status; the pill derives its parent group
 * from it. Selecting "All" inside a parent reverts to the parent's
 * 'open' or 'closed' literal so a bookmark to the parent stays stable.
 *
 * Legacy alias matrix (preserves existing bookmarks):
 *   ?status=fixed     -> 'resolved'
 *   ?status=active    -> 'open'   (now ambiguous against needs_review,
 *                                  but kept for one release as the
 *                                  Open-group fallback)
 *   absent / unknown  -> 'open'   (default view)
 */

export type ExceptionStatus = 'open' | 'closed'

export type ExceptionStatusValue =
  | 'open'
  | 'needs_review'
  | 'blocked'
  | 'closed'
  | 'resolved'
  | 'ignored'

const OPEN_SUB = new Set<ExceptionStatusValue>([
  'open',
  'needs_review',
  'blocked',
])
const CLOSED_SUB = new Set<ExceptionStatusValue>([
  'closed',
  'resolved',
  'ignored',
])

const VALID_SUB: Set<ExceptionStatusValue> = new Set([
  ...OPEN_SUB,
  ...CLOSED_SUB,
])

export const readExceptionStatusValue = (
  params: URLSearchParams,
): ExceptionStatusValue => {
  const raw = params.get('status')
  if (!raw) return 'open'
  const lowered = raw.toLowerCase()
  if (lowered === 'active') return 'open'
  if (lowered === 'fixed') return 'resolved'
  // Hyphenated alias for the canonical snake_case wire value, so a URL
  // a human typed (e.g. ?status=needs-review) still lands correctly.
  // We always write the snake_case form when mutating the URL.
  if (lowered === 'needs-review') return 'needs_review'
  if (VALID_SUB.has(lowered as ExceptionStatusValue)) {
    return lowered as ExceptionStatusValue
  }
  return 'open'
}

export const getStatusGroup = (value: ExceptionStatusValue): ExceptionStatus =>
  CLOSED_SUB.has(value) ? 'closed' : 'open'

export const readExceptionStatus = (
  params: URLSearchParams,
): ExceptionStatus => getStatusGroup(readExceptionStatusValue(params))
